import React from 'react';
import './SelectDeployPanel.less';
import HwzComponent from '../../../common/component/HwzComponent';
import { Button, Progress, Alert, Space, Spin, Card, Row, Col } from 'antd';
import Lang from '../../../common/model/global/Lang';
import MessageBoxUtil from '../../../common/util/MessageBoxUtil';
import Install from '../../../common/model/install/Install';
import { DeploymentUnitOutlined, DatabaseOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons/lib';

interface IProps {
  install: Install;
  onNextStep: () => void;
}

interface IState {
  deploying: boolean;      // 是否部署中
  step: number;            // 当前步骤 0-开始,1-连接数据库,2-建表,3-创建管理员,4-完成
  errorMsg: string;        // 错误信息
  stepStatus: ('wait' | 'process' | 'finish' | 'error')[];
  selectedDbType: 'mysql' | 'sqlite' | null; // 用户选择的数据库类型
}

// ================= 厂商可配置的默认 MySQL 参数 =================
const DEFAULT_MYSQL_CONFIG = {
  host: 'localhost',
  port: 3399,
  schema: 'hwz_db',
  username: 'hwzAdmin',
  password: 'Etitle1804#',
  charset: 'utf8mb4',
};

const DEFAULT_ADMIN = {
  username: 'admin',
  password: 'HWZ@1234',
};

export default class SelectDeployPanel extends HwzComponent<IProps, IState> {
  private install: Install;

  constructor(props: IProps) {
    super(props);
    this.install = this.props.install;
    this.state = {
      deploying: false,
      step: 0,
      errorMsg: '',
      stepStatus: ['wait', 'wait', 'wait', 'wait', 'wait'],
      selectedDbType: null,
    };
  }

  componentDidMount() {
    this.updateUI();
  }

  private promiseHttpVerify = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.install.httpVerify(
        () => resolve(),
        (msg: string) => reject(new Error(msg))
      );
    });
  };

  private promiseHttpCreateTable = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.install.httpCreateTable(
        () => resolve(),
        (msg: string) => reject(new Error(msg)),
        () => {}
      );
    });
  };

  private promiseHttpCreateAdmin = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.install.httpCreateAdmin(
        () => resolve(),
        (msg: string) => reject(new Error(msg))
      );
    });
  };

  private promiseHttpFinish = (): Promise<void> => {
    return new Promise((resolve, reject) => {
      this.install.httpFinish(
        () => resolve(),
        (msg: string) => reject(new Error(msg)),
        () => {}
      );
    });
  };

  private setStep(index: number, status: 'wait' | 'process' | 'finish' | 'error') {
    const newStatus = [...this.state.stepStatus];
    newStatus[index] = status;
    this.setState({ stepStatus: newStatus, step: index });
    this.updateUI();
  }

  private configureDatabase(dbType: 'mysql' | 'sqlite') {
    if (dbType === 'mysql') {
      this.install.dbType = 'mysql';
      this.install.mysqlHost = DEFAULT_MYSQL_CONFIG.host;
      this.install.mysqlPort = DEFAULT_MYSQL_CONFIG.port;
      this.install.mysqlSchema = DEFAULT_MYSQL_CONFIG.schema;
      this.install.mysqlUsername = DEFAULT_MYSQL_CONFIG.username;
      this.install.mysqlPassword = DEFAULT_MYSQL_CONFIG.password;
      this.install.mysqlCharset = DEFAULT_MYSQL_CONFIG.charset;
    } else {
      this.install.dbType = 'sqlite';
    }
    this.install.verified = false;
    this.install.adminConfigured = false;
  }

  private async testDatabaseConnection(dbType: 'mysql' | 'sqlite') {
    if (dbType === 'sqlite') {
      this.install.verified = true;
      this.setStep(1, 'finish');
      return;
    }
    await this.promiseHttpVerify();
    this.install.verified = true;
    this.setStep(1, 'finish');
  }

  private async ensureTables() {
    await new Promise<void>((resolve, reject) => {
      this.install.httpTableInfoList(
        () => resolve(),
        (msg: string) => reject(new Error(msg))
      );
    });
    if (this.install.tableCreated()) {
      this.setStep(2, 'finish');
      return;
    }
    await this.promiseHttpCreateTable();
    this.setStep(2, 'finish');
  }


private async ensureAdmin() {
  this.install.adminUsername = DEFAULT_ADMIN.username;
  this.install.adminPassword = DEFAULT_ADMIN.password;
  this.install.adminRepassword = DEFAULT_ADMIN.password;

  if (this.install.adminConfigured) {
      this.setStep(3, 'finish');
      return;
  }

  try {
      await this.promiseHttpCreateAdmin();
      this.install.adminConfigured = true;
      this.setStep(3, 'finish');
  } catch (error: any) {
      const errorMsg = error.message || '';
      if (errorMsg.includes('已存在') || errorMsg.includes('already exist')) {
          console.warn('管理员账号已存在，跳过创建步骤', errorMsg);
          this.install.adminConfigured = true;
          this.setStep(3, 'finish');
          return;
      }
      throw error;
  }
}



  private async completeInstallation() {
    await this.promiseHttpFinish();
  }

  public startFastDeploy = async (dbType: 'mysql' | 'sqlite') => {
    if (this.state.deploying) return;

    this.setState({
      deploying: true,
      errorMsg: '',
      step: 1,
      stepStatus: ['finish', 'process', 'wait', 'wait', 'wait'],
      selectedDbType: dbType,
    });
    this.updateUI();

    try {
      this.configureDatabase(dbType);
      this.setStep(1, 'process');
      await this.testDatabaseConnection(dbType);
      this.setStep(2, 'process');
      await this.ensureTables();
      this.setStep(3, 'process');
      await this.ensureAdmin();
      this.setStep(4, 'process');
      await this.completeInstallation();
      this.setStep(4, 'finish');
      MessageBoxUtil.success('部署成功！即将进入首页...');
      setTimeout(() => {
        window.location.href = '/';
      }, 1000);
    } catch (error: any) {
      const errorMessage = error.message || '部署失败，请检查网络或联系管理员';
      this.setState({ errorMsg: errorMessage, deploying: false });
      this.setStep(this.state.step, 'error');
      MessageBoxUtil.error(errorMessage);
      this.updateUI();
    }
  };

  render() {
    const { deploying, step, errorMsg, stepStatus, selectedDbType } = this.state;

    // 未开始部署：显示两个卡片选择
    if (!deploying && !errorMsg) {
      return (
        <div className="widget-deploy-panel" style={{ padding: '24px', textAlign: 'center' }}>
          <h2>选择快速部署方式</h2>
          <Row gutter={16} style={{ marginTop: 32 }}>
            <Col span={12}>
              <Card
                hoverable
                style={{ textAlign: 'center', cursor: 'pointer' }}
                onClick={() => this.startFastDeploy('sqlite')}
              >
                <DatabaseOutlined style={{ fontSize: 48, color: '#1890ff' }} />
                <h3 style={{ marginTop: 16 }}>SQLite 快速部署</h3>
                <p>零配置，适合数据量不大的环境</p>
                <Button type="primary" ghost>
                  立即部署
                </Button>
              </Card>
            </Col>
            <Col span={12}>
              <Card
                hoverable
                style={{ textAlign: 'center', cursor: 'pointer' }}
                onClick={() => this.startFastDeploy('mysql')}
              >
                <DatabaseOutlined style={{ fontSize: 48, color: '#52c41a' }} />
                <h3 style={{ marginTop: 16 }}>MySQL 快速部署</h3>
                <p>使用预设参数，适合生产环境</p>
                <Button type="primary" ghost>
                  立即部署
                </Button>
              </Card>
            </Col>
          </Row>
          <div style={{ marginTop: 32, color: '#999', fontSize: 12 }}>
            MySQL 部署将使用厂商预设的连接参数，您无需手动配置。
          </div>
        </div>
      );
    }

    // 部署进行中或失败后的进度显示
    const isMysql = selectedDbType === 'mysql';
    return (
      <div className="widget-deploy-panel" style={{ padding: '24px', textAlign: 'center' }}>
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
          <div>
            <h2>{isMysql ? 'MySQL 快速部署中' : 'SQLite 快速部署中'}</h2>
            <p style={{ color: '#666' }}>
              {isMysql
                ? `正在使用预设 MySQL 配置自动部署...`
                : `正在使用 SQLite 数据库自动部署...`}
            </p>
          </div>

          <div style={{ width: '80%', margin: '0 auto' }}>
            <Progress
              percent={Math.floor((step / 4) * 100)}
              status={errorMsg ? 'exception' : 'active'}
            />
            <div style={{ marginTop: 24, textAlign: 'left' }}>
              <div style={{ marginBottom: 12 }}>
                <StepIcon status={stepStatus[1]} /> 数据库连接
              </div>
              <div style={{ marginBottom: 12 }}>
                <StepIcon status={stepStatus[2]} /> 创建数据表
              </div>
              <div style={{ marginBottom: 12 }}>
                <StepIcon status={stepStatus[3]} /> 创建管理员
              </div>
              <div style={{ marginBottom: 12 }}>
                <StepIcon status={stepStatus[4]} /> 完成安装
              </div>
            </div>
          </div>

          {errorMsg && (
            <Alert
              message="部署失败"
              description={errorMsg}
              type="error"
              showIcon
              action={
                <Button
                  size="small"
                  type="primary"
                  onClick={() => {
                    this.setState({
                      deploying: false,
                      errorMsg: '',
                      step: 0,
                      stepStatus: ['wait', 'wait', 'wait', 'wait', 'wait'],
                      selectedDbType: null,
                    });
                    this.updateUI();
                  }}
                >
                  重新选择
                </Button>
              }
            />
          )}
        </Space>
      </div>
    );
  }
}

const StepIcon: React.FC<{ status: 'wait' | 'process' | 'finish' | 'error' }> = ({ status }) => {
  if (status === 'process') return <Spin size="small" style={{ marginRight: 8 }} />;
  if (status === 'finish') return <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />;
  if (status === 'error') return <CloseCircleOutlined style={{ color: '#ff4d4f', marginRight: 8 }} />;
  return <span style={{ display: 'inline-block', width: 16, marginRight: 8 }}>○</span>;
};